import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, BugReport, Delete, Edit, MoreVert, WarningAmberRounded } from "@mui/icons-material";
import {
  useGetSnagsQuery,
  useCreateSnagMutation,
  useUpdateSnagMutation,
  useDeleteSnagMutation,
  ProTrackSnag,
  SnagCategory,
  SnagSeverity,
  SnagStatus,
} from "../../../redux/protrackApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";

const NAVY = "#123456";
const TODAY = new Date().toISOString().slice(0, 10);

const CATEGORIES: { value: SnagCategory; label: string }[] = [
  { value: "STRUCTURAL", label: "Structural" },
  { value: "ELECTRICAL", label: "Electrical" },
  { value: "PLUMBING", label: "Plumbing" },
  { value: "FINISHES", label: "Finishes" },
  { value: "DOORS_WINDOWS", label: "Doors & Windows" },
  { value: "ROOFING", label: "Roofing" },
  { value: "EXTERNAL", label: "External" },
  { value: "OTHER", label: "Other" },
];

const INSPECTION_TYPES = [
  { value: "INITIAL", label: "Initial inspection" },
  { value: "RE_INSPECTION", label: "Re-inspection" },
  { value: "FINAL_HANDOVER", label: "Final handover" },
  { value: "FOLLOW_UP", label: "Follow-up" },
] as const;

const TRADE_OPTIONS = [
  "General Builder",
  "Electrical",
  "Plumbing",
  "Roofing",
  "Brickwork",
  "Plastering",
  "Joinery",
  "Painting",
  "Landscaping",
  "Glazing",
  "External Works",
  "Other",
];

const SEVERITY_CONFIG: Record<SnagSeverity, { label: string; color: string }> = {
  LOW: { label: "Low", color: "#2e7d32" },
  MEDIUM: { label: "Medium", color: "#f57c00" },
  HIGH: { label: "High", color: "#c62828" },
  CRITICAL: { label: "Critical", color: "#4a148c" },
};

const STATUS_CONFIG: Record<SnagStatus, { label: string; color: string }> = {
  OPEN: { label: "Open", color: "#c62828" },
  IN_PROGRESS: { label: "In Progress", color: "#1565c0" },
  RESOLVED: { label: "Resolved", color: "#2e7d32" },
  VERIFIED: { label: "Verified", color: "#1b5e20" },
  CLOSED: { label: "Closed", color: "#78909c" },
};

const emptyForm = (): Partial<ProTrackSnag> => ({
  blockId: "",
  siteName: "",
  unitNumber: "",
  title: "",
  description: "",
  category: "OTHER",
  severity: "MEDIUM",
  status: "OPEN",
  inspectionType: "INITIAL",
  reportedDate: TODAY,
  dueDate: "",
  reinspectionDate: "",
  reportedBy: "",
  assignedTo: "",
  subcontractor: "",
  location: "",
  trade: "General Builder",
  rootCause: "",
  actionRequired: "",
  closureComment: "",
  notes: "",
});

export const ProTrackSnagList: React.FC = () => {
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [blockFilter, setBlockFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const siteOptions = Array.from(new Set(blocks.map((b) => b.development).filter(Boolean))) as string[];

  const { data: snags = [], isLoading } = useGetSnagsQuery({
    blockId: blockFilter || undefined,
    status: statusFilter || undefined,
  });

  const filteredSnags = snags.filter((s) => {
    const matchesSite = siteFilter ? (s.siteName ?? s.blockName ?? "") === siteFilter || (s.blockId && blocks.find((b) => b.id === s.blockId)?.development === siteFilter) : true;
    return matchesSite;
  });
  const [createSnag, { isLoading: creating }] = useCreateSnagMutation();
  const [updateSnag, { isLoading: updating }] = useUpdateSnagMutation();
  const [deleteSnag] = useDeleteSnagMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProTrackSnag | null>(null);
  const [form, setForm] = useState<Partial<ProTrackSnag>>(emptyForm());
  const [formError, setFormError] = useState("");

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<ProTrackSnag | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm());
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (s: ProTrackSnag) => {
    setMenuAnchor(null);
    setEditTarget(s);
    setForm({ ...s });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.blockId) { setFormError("Select a project block."); return; }
    if (!form.title?.trim()) { setFormError("Title is required."); return; }
    setFormError("");
    const payload = {
      blockId: form.blockId!,
      blockName: blocks.find((b) => b.id === form.blockId)?.name || undefined,
      siteName: blocks.find((b) => b.id === form.blockId)?.development || form.siteName || undefined,
      unitNumber: form.unitNumber || undefined,
      title: form.title!,
      description: form.description || undefined,
      category: (form.category as SnagCategory) ?? "OTHER",
      severity: (form.severity as SnagSeverity) ?? "MEDIUM",
      status: (form.status as SnagStatus) ?? "OPEN",
      inspectionType: (form.inspectionType as any) ?? "INITIAL",
      reportedDate: form.reportedDate ?? TODAY,
      dueDate: form.dueDate || undefined,
      resolvedDate: form.resolvedDate || undefined,
      reinspectionDate: form.reinspectionDate || undefined,
      reportedBy: form.reportedBy || undefined,
      assignedTo: form.assignedTo || undefined,
      subcontractor: form.subcontractor || undefined,
      location: form.location || undefined,
      trade: form.trade || undefined,
      rootCause: form.rootCause || undefined,
      actionRequired: form.actionRequired || undefined,
      closureComment: form.closureComment || undefined,
      notes: form.notes || undefined,
    };
    try {
      if (editTarget?.id) {
        await updateSnag({ id: editTarget.id, data: payload }).unwrap();
      } else {
        await createSnag(payload).unwrap();
      }
      setDialogOpen(false);
    } catch {
      setFormError("Failed to save. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (menuTarget?.id) await deleteSnag(menuTarget.id);
    setDeleteConfirmOpen(false);
    setMenuAnchor(null);
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Site</InputLabel>
            <Select label="Site" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
              <MenuItem value="">All Sites</MenuItem>
              {siteOptions.map((site) => (
                <MenuItem key={site} value={site}>{site}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Block</InputLabel>
            <Select label="Filter by Block" value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)}>
              <MenuItem value="">All Blocks</MenuItem>
              {blocks.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All Statuses</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([v, sc]) => (
                <MenuItem key={v} value={v}>{sc.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
        >
          Log Snag
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : filteredSnags.length === 0 ? (
        <Box textAlign="center" py={8} color="text.secondary">
          <BugReport sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography mt={1}>No snags found for the selected filter. Log the first one.</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {filteredSnags.map((s) => {
            const sc = STATUS_CONFIG[s.status as SnagStatus] ?? STATUS_CONFIG.OPEN;
            const sv = SEVERITY_CONFIG[s.severity as SnagSeverity] ?? SEVERITY_CONFIG.MEDIUM;
            return (
              <Box
                key={s.id}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderLeft: `4px solid ${sv.color}`,
                  borderRadius: 2,
                  p: 2,
                  bgcolor: "#fff",
                  "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                }}
              >
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography fontWeight={700}>{s.title}</Typography>
                      <Chip label={sv.label} size="small" sx={{ bgcolor: `${sv.color}22`, color: sv.color, fontWeight: 600, fontSize: 11 }} />
                      <Chip label={sc.label} size="small" sx={{ bgcolor: `${sc.color}22`, color: sc.color, fontWeight: 600, fontSize: 11 }} />
                      <Chip label={CATEGORIES.find((c) => c.value === s.category)?.label ?? s.category} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      {s.inspectionType && (
                        <Chip label={s.inspectionType.replace('_', ' ')} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      )}
                    </Box>
                    <Box display="flex" gap={3} mt={0.5} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        Site: {s.siteName || blocks.find((b) => b.id === s.blockId)?.development || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Block: {s.blockName ?? s.blockId}
                        {s.unitNumber ? ` · Unit: ${s.unitNumber}` : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Reported: {s.reportedDate}
                      </Typography>
                      {s.dueDate && (
                        <Typography variant="caption" color={s.dueDate < TODAY && s.status !== "CLOSED" ? "error" : "text.secondary"}>
                          Due: {s.dueDate}
                        </Typography>
                      )}
                      {s.assignedTo && (
                        <Typography variant="caption" color="text.secondary">
                          Assigned: {s.assignedTo}
                        </Typography>
                      )}
                    </Box>
                    {(s.location || s.trade || s.actionRequired || s.rootCause) && (
                      <Box display="flex" gap={2} mt={0.75} flexWrap="wrap">
                        {s.location && <Typography variant="caption" color="text.secondary">Location: {s.location}</Typography>}
                        {s.trade && <Typography variant="caption" color="text.secondary">Trade: {s.trade}</Typography>}
                        {s.rootCause && <Typography variant="caption" color="text.secondary">Root cause: {s.rootCause}</Typography>}
                        {s.actionRequired && <Typography variant="caption" color="text.secondary">Action: {s.actionRequired}</Typography>}
                      </Box>
                    )}
                    {s.description && (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {s.description}
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(s); }}>
                    <MoreVert />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => menuTarget && openEdit(menuTarget)}>
          <Edit sx={{ mr: 1, fontSize: 18 }} /> Edit
        </MenuItem>
        <MenuItem sx={{ color: "error.main" }} onClick={() => { setDeleteConfirmOpen(true); setMenuAnchor(null); }}>
          <Delete sx={{ mr: 1, fontSize: 18 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Snag</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{menuTarget?.title}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberRounded fontSize="small" color="warning" />
          {editTarget ? "Edit Snag" : "Log Snag"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2} flexWrap="wrap">
              <FormControl sx={{ flex: 1, minWidth: 240 }} size="small" required>
                <InputLabel>Site</InputLabel>
                <Select label="Site" value={form.siteName ?? (blocks.find((b) => b.id === form.blockId)?.development ?? "")} onChange={(e) => setForm((f) => ({ ...f, siteName: e.target.value }))}>
                  {siteOptions.map((site) => <MenuItem key={site} value={site}>{site}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1.5, minWidth: 280 }} size="small" required>
                <InputLabel>Project Block *</InputLabel>
                <Select label="Project Block *" value={form.blockId ?? ""} onChange={(e) => setForm((f) => ({ ...f, blockId: e.target.value, siteName: blocks.find((b) => b.id === e.target.value)?.development || f.siteName }))}>
                  {blocks.map((b) => (
                    <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Unit Number" size="small" fullWidth value={form.unitNumber ?? ""} onChange={(e) => setForm((f) => ({ ...f, unitNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Location / Area" size="small" fullWidth value={form.location ?? ""} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Title *" size="small" fullWidth value={form.title ?? ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Detailed description" size="small" fullWidth multiline rows={3} value={form.description ?? ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select label="Category" value={form.category ?? "OTHER"} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as SnagCategory }))}>
                    {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Trade</InputLabel>
                  <Select label="Trade" value={form.trade ?? "General Builder"} onChange={(e) => setForm((f) => ({ ...f, trade: e.target.value }))}>
                    {TRADE_OPTIONS.map((trade) => <MenuItem key={trade} value={trade}>{trade}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Severity</InputLabel>
                  <Select label="Severity" value={form.severity ?? "MEDIUM"} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value as SnagSeverity }))}>
                    {Object.entries(SEVERITY_CONFIG).map(([v, sc]) => <MenuItem key={v} value={v}>{sc.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={form.status ?? "OPEN"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as SnagStatus }))}>
                    {Object.entries(STATUS_CONFIG).map(([v, sc]) => <MenuItem key={v} value={v}>{sc.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Inspection type</InputLabel>
                  <Select label="Inspection type" value={form.inspectionType ?? "INITIAL"} onChange={(e) => setForm((f) => ({ ...f, inspectionType: e.target.value as any }))}>
                    {INSPECTION_TYPES.map((i) => <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Reported date *" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.reportedDate ?? TODAY} onChange={(e) => setForm((f) => ({ ...f, reportedDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Due date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.dueDate ?? ""} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Reinspection date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.reinspectionDate ?? ""} onChange={(e) => setForm((f) => ({ ...f, reinspectionDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Resolved date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.resolvedDate ?? ""} onChange={(e) => setForm((f) => ({ ...f, resolvedDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Reported by" size="small" fullWidth value={form.reportedBy ?? ""} onChange={(e) => setForm((f) => ({ ...f, reportedBy: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Assigned to" size="small" fullWidth value={form.assignedTo ?? ""} onChange={(e) => setForm((f) => ({ ...f, assignedTo: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Subcontractor" size="small" fullWidth value={form.subcontractor ?? ""} onChange={(e) => setForm((f) => ({ ...f, subcontractor: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Root cause" size="small" fullWidth value={form.rootCause ?? ""} onChange={(e) => setForm((f) => ({ ...f, rootCause: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Action required" size="small" fullWidth value={form.actionRequired ?? ""} onChange={(e) => setForm((f) => ({ ...f, actionRequired: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Closure comment" size="small" fullWidth value={form.closureComment ?? ""} onChange={(e) => setForm((f) => ({ ...f, closureComment: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notes" size="small" fullWidth multiline rows={2} value={form.notes ?? ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </Grid>
            </Grid>

            {formError && <Typography color="error" variant="caption">{formError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}>
            {creating || updating ? <CircularProgress size={18} /> : editTarget ? "Save Changes" : "Log Snag"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
